import { Customer } from "@/types/customer";
import { Product } from "@/types/product";
import { User, AuthSession } from "@/types/auth";
import { generateUUID } from "@/lib/uuid";

// KV Service for Cloudflare Workers
class KVService {
  private baseUrl: string;
  private token: string;

  constructor() {
    this.baseUrl = import.meta.env.VITE_KV_API_URL || '';
    this.token = import.meta.env.VITE_KV_TOKEN || '';
  }

  private async request(endpoint: string, options: RequestInit = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const response = await fetch(url, {
      ...options,
      headers: {
        'Authorization': `Bearer ${this.token}`,
        'X-API-Token': this.token,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      throw new Error(`KV request failed: ${response.status} - ${errorText}`);
    }

    return response.json();
  }

  // Auth methods
  async getUser(username: string): Promise<User | null> {
    try {
      const data = await this.request(`/users/${username}`);
      return data ? { ...data, createdAt: new Date(data.createdAt), lastLogin: data.lastLogin ? new Date(data.lastLogin) : undefined } : null;
    } catch {
      return null;
    }
  }

  async createUser(user: Omit<User, "id" | "createdAt">): Promise<User> {
    const newUser: User = {
      ...user,
      id: generateUUID(),
      createdAt: new Date(),
    };
    await this.request(`/users/${user.username}`, {
      method: 'PUT',
      body: JSON.stringify(newUser),
    });
    return newUser;
  }

  async updateUserLastLogin(username: string): Promise<void> {
    const user = await this.getUser(username);
    if (user) {
      user.lastLogin = new Date();
      await this.request(`/users/${username}`, {
        method: 'PUT',
        body: JSON.stringify(user),
      });
    }
  }

  // Session methods
  async createSession(session: AuthSession): Promise<void> {
    await this.request(`/sessions/${session.token}`, {
      method: 'PUT',
      body: JSON.stringify(session),
    });
  }

  async getSession(token: string): Promise<AuthSession | null> {
    try {
      const data = await this.request(`/sessions/${token}`);
      return data ? { 
        ...data, 
        user: { ...data.user, createdAt: new Date(data.user.createdAt), lastLogin: data.user.lastLogin ? new Date(data.user.lastLogin) : undefined },
        expiresAt: new Date(data.expiresAt) 
      } : null;
    } catch {
      return null;
    }
  }

  async deleteSession(token: string): Promise<void> {
    await this.request(`/sessions/${token}`, {
      method: 'DELETE',
    });
  }

  // Customer methods
  async getCustomers(): Promise<Customer[]> {
    try {
      const data = await this.request('/customers');
      return data ? data.map((c: any) => ({ ...c, createdAt: new Date(c.createdAt) })) : [];
    } catch {
      return [];
    }
  }

  async createCustomer(customer: Customer): Promise<void> {
    await this.request('/customers', {
      method: 'POST',
      body: JSON.stringify(customer),
    });
  }

  async updateCustomer(customer: Customer): Promise<void> {
    await this.request(`/customers/${customer.id}`, {
      method: 'PUT',
      body: JSON.stringify(customer),
    });
  }

  async deleteCustomer(id: string): Promise<void> {
    await this.request(`/customers/${id}`, {
      method: 'DELETE',
    });
  }

  // Product methods
  async getProducts(): Promise<Product[]> {
    try {
      const data = await this.request('/products');
      return data ? data.map((p: any) => ({ ...p, createdAt: new Date(p.createdAt) })) : [];
    } catch {
      return [];
    }
  }

  async createProduct(product: Product): Promise<void> {
    await this.request('/products', {
      method: 'POST',
      body: JSON.stringify(product),
    });
  }

  async updateProduct(product: Product): Promise<void> {
    await this.request(`/products/${product.id}`, {
      method: 'PUT',
      body: JSON.stringify(product),
    });
  }

  async deleteProduct(id: string): Promise<void> {
    await this.request(`/products/${id}`, {
      method: 'DELETE',
    });
  }

  // Jobs methods
  async getJobs(): Promise<string[]> {
    try {
      const data = await this.request('/jobs');
      return data || [];
    } catch {
      return [];
    }
  }

  async updateJobs(jobs: string[]): Promise<void> {
    await this.request('/jobs', {
      method: 'PUT',
      body: JSON.stringify(jobs),
    });
  }
}

export const kvService = new KVService();